import { COMPANY_NAME, Role, type User } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { DateTime } from '@piccolohealth/util';
import React from 'react';
import { FaRegClock } from 'react-icons/fa';
import { JsonTree } from '../../components/generic/JsonTree';
import { UserAvatar } from '../../components/user/UserAvatar';
import { useRole } from '../../hooks/useRole';

interface Props {
  timestamp: DateTime;
  message: string;
  user?: User;
  payload: any;
}

export const ParticipantTimelineItem = (props: Props) => {
  const { timestamp, message, user, payload } = props;

  const disclosure = ChakraV3.useDisclosure();
  const date = DateTime.fromISO(timestamp.toString());
  const hasSupportRole = useRole(Role.Support);

  // Show a relative string if younger than 24 hours, otherwise, show a  DateTime.DATETIME_SHORT
  const formattedDate =
    date.diffNow().as('hours') > -24
      ? date.toRelative()
      : date.toLocaleString(DateTime.DATETIME_SHORT);

  return (
    <ChakraV3.Stack>
      <ChakraV3.HStack py={2} px={6} gap={4} align='center'>
        <UserAvatar
          name={user?.name ?? COMPANY_NAME}
          picture={user?.picture}
          secondary={user?.email ?? undefined}
          showTooltip={false}
          size='md'
        />

        <ChakraV3.Stack gap={0}>
          <ChakraV3.HStack align='baseline'>
            <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
              {user?.name}
            </ChakraV3.Text>
            <ChakraV3.Text fontSize='xs' color='secondary'>
              {formattedDate}
            </ChakraV3.Text>
          </ChakraV3.HStack>
          <ChakraV3.Stack
            fontSize='sm'
            color='gray.700'
            css={{
              a: {
                fontWeight: 'bold',
                color: 'purple.600',
                _hover: {
                  textDecoration: 'underline',
                },
              },
            }}
          >
            {message}
          </ChakraV3.Stack>
        </ChakraV3.Stack>
        <ChakraV3.Spacer />
        {hasSupportRole && (
          <ChakraV3.IconButton
            onClick={disclosure.onToggle}
            color='gray.400'
            size='sm'
            variant='ghost'
            aria-label='History'
          >
            <FaRegClock />
          </ChakraV3.IconButton>
        )}
      </ChakraV3.HStack>
      {disclosure.open && hasSupportRole && (
        <ChakraV3.Collapsible.Root open={disclosure.open}>
          <ChakraV3.Collapsible.Content>
            <ChakraV3.Stack w='lg'>
              <JsonTree
                src={{
                  timestamp,
                  message,
                  user,
                  payload,
                }}
                enableClipboard={false}
              />
            </ChakraV3.Stack>
          </ChakraV3.Collapsible.Content>
        </ChakraV3.Collapsible.Root>
      )}
    </ChakraV3.Stack>
  );
};
